import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api, setAccessTokens } from "../../utils/api";
import AppContext from "../../utils/AppContext";
import { IS_DEV } from "../../utils/envSettings";
import { setLanguage } from "../../locale/localeUtils";
import useCurrentUser from "../../hooks/useCurrentUser";
import { useGoogleLogin } from "@react-oauth/google";
import i18next from "i18next";
import { capitalize, randomString } from "../../utils/userUtils";

import { Trans as T, useTranslation } from 'react-i18next';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import PasswordInput from "../../common/PasswordInput/PasswordInput";

import { ReactComponent as GoogleLogo } from "../../assets/icons_svg/google_logo.svg";

import './AuthForm.scss';

const DEV_USERS = [
  'andres@preciousleads.io',
  'yuan@preciousleads.io',
  'alejandro@preciousleads.io',
  'magda@preciousleads.io',
  'yiwen@preciousleads.io',
  'karen@preciousleads.io',
  'ricky@preciousleads.io',
  'raul@preciousleads.io',
];

const autoUser = IS_DEV && process.env.REACT_APP_AUTO_USER_SIGN_UP === 'true';

export default function AuthForm({ type, hideTitle, signUpText, bare }) {

  const [email, setEmail] = useState(() => autoUser ? (randomString() + "@random.com") : '');
  const [firstName, setFirstName] = useState(() => autoUser ? capitalize(randomString(5)) : '');
  const [lastName, setLastName] = useState(() => autoUser ? capitalize(randomString(5)) : '');
  const [password, setPassword] = useState(() => autoUser ? 'secretpassword': '');
  const [showDevUsers, setShowDevUsers] = useState(false);
  const [devUser, setDevUser] = useState(localStorage.getItem('pl__dev_default_user') || DEV_USERS[0]);
  const [errors, setErrors] = useState(null);
  
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(null);

  const [, dispatch] = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const { updateUser } = useCurrentUser();

  const [searchParams] = useSearchParams();
  const fromInvite = searchParams.get('from_invite');

  const { t } = useTranslation();

  const isLogin = type === 'login';

  // NOTE -> this could be done inside the useState default, but since it only happens on DEV it's better to prevent:
  useEffect(() => {
    if (IS_DEV && isLogin && localStorage.getItem('pl__show_dev_users') !== 'false') {
      const user = localStorage.getItem('pl__dev_default_user') || DEV_USERS[0];

      setShowDevUsers(true);
      setDevUser(user);
      setEmail(user);
      setPassword('secretpassword');
    }
  }, [isLogin]);

  const handleRequestError = e => {
    console.log('e:', e)
    // Server down:
    if (e?.code === 'ERR_NETWORK' || e?.response?.status === 500) {
      setErrors({ non_field_errors: ["The request could not be processed. Refresh this page or try again momentarily."] });
      setValidated(false); // do not show the form as valid if this happens
    }
    else if (e?.response?.data) {
      setErrors(e.response.data);
    }
    else {
      setErrors({ non_field_errors: ["The request could not be processed. Refresh this page or try again momentarily."] });
      setValidated(false); // do not show the ✔️ checks on form as valid if this happens
    }
  }

  const handleSubmit = async e => {
    e.preventDefault();
    setErrors(null);

    // this validation might be tricky, because it leads to the user to think that empty spaces (first_name.trim().length === 0) are valid
    // or emails such as me@there
    // const form = e.currentTarget;
    //
    // if (!form.checkValidity()) {
    //   setValidated(true);
    //   return;
    // }

    setLoading(true);

    isLogin ? await loginUser() : await registerUser();

    setLoading(false);
  }

  const registerUser = async () => {
    setValidated(false);

    try {
      await axios.post(process.env.REACT_APP_API_URL + '/register/', { email, password, first_name: firstName, last_name: lastName }, { headers: { 'Accept-Language': i18next.language }});

      // NOTE: Auth tokens use Form URL Encoded, NOT JSON as a body (see the Bruno request). JSON will not work:
      const params = new URLSearchParams();
      params.append('client_id', process.env.REACT_APP_API_CLIENT_ID);
      params.append('username', email);
      params.append('password', password);
      params.append('grant_type', 'password');
      
      const { data } = await axios.post(process.env.REACT_APP_API_URL + '/auth/token/', params);
      setAccessTokens(data.access_token, data.refresh_token);
      
      const { data: userData } = await api.get('/users/me/');

      setAccessTokens(data.access_token, data.refresh_token, (!userData.onboarding && !fromInvite) ? 3 : 365);

      const settings = { language: i18next.language };

      // if the user came from an invitation, set onboarding to the invitation:
      if (fromInvite === 'true') {
        await updateUser({ onboarding: { flow: 'from_invite', done: true }, settings }, userData.id);
      }

      // if no user onboarding data exists, add the default onboarding flow. The AuthRoute will automatically redirect the user:
      else if (!userData.onboarding) {
        await updateUser({
          onboarding          : { flow: 'mvp_free_plan', step: 'name_business' },
          email_notifications : { product: true, marketing : true },
          settings,
        }, userData.id);

        return;
      }
      // has onboarding, save the currentUser inside the context:
      else {
        await dispatch({ type: 'currentUser/set', payload: userData });
      }

      // TODO -> register maybe should not allow users to redirect because we have an onboarding flow
      const redirectTo = searchParams.get('redirect_to');

      if (redirectTo) {
        navigate(redirectTo ? decodeURIComponent(redirectTo) : '/');
      }
    }
    catch (e) {
      setValidated(true);
      handleRequestError(e);
    }
  }

  const loginUser = async () => {
    try {
      // NOTE: Auth tokens use Form URL Encoded, NOT JSON as a body (see the Bruno request). JSON will not work:
      const params = new URLSearchParams();
      params.append('client_id', process.env.REACT_APP_API_CLIENT_ID);
      params.append('username', email);
      params.append('password', password);
      params.append('grant_type', 'password');
      
      const { data } = await axios.post(process.env.REACT_APP_API_URL + '/auth/token/', params);

      setAccessTokens(data.access_token, data.refresh_token);
            
      const { data: userData } = await api.get('/users/me/');
      await dispatch({ type: 'currentUser/set', payload: userData });

      setLanguage(userData.settings.language);

      // if the user came from an invitation, set onboarding to the invitation:
      if (fromInvite === 'true') {
        await updateUser({ onboarding: { flow: 'from_invite', done: true }}, userData.id);
      }

      const redirectTo = searchParams.get('redirect_to');

      if (redirectTo) {
        navigate(redirectTo ? decodeURIComponent(redirectTo) : '/');
      }
    }
    catch (e) {
      handleRequestError(e);
    }
  }

  const toggleShowDevUsers = () => {
    if (!showDevUsers) {
      setEmail(devUser);
      setPassword('password');
    }

    setShowDevUsers(!showDevUsers);
    localStorage.setItem('pl__show_dev_users', !showDevUsers);
  }

  const selectDevUser = e => {
    localStorage.setItem('pl__dev_default_user', e.target.value);
    setDevUser(e.target.value);
    setEmail(e.target.value);
    setPassword('secretpassword');
  }

  const onGoogleLogin = async response => {
    const params = new URLSearchParams();

    params.append('client_id', process.env.REACT_APP_API_CLIENT_ID);
    params.append('backend', 'google-oauth2');
    params.append('token', response.access_token);
    params.append('grant_type', 'convert_token');
   
    const { data } = await axios.post(process.env.REACT_APP_API_URL + '/social-auth/convert-token/', params);

    const { data: userData } = await api.get('/users/me/');

    setAccessTokens(data.access_token, data.refresh_token, (!userData.onboarding && !fromInvite) ? 3 : 365);
    
    setLanguage(userData.settings.language);
    
    const settings = { language: i18next.language };
    const email_notifications = { product: true, marketing : true };

    // if the user came from an invitation, set onboarding to the invitation:
    if (fromInvite === 'true') {
      const updateData = {
        onboarding: { flow: 'from_invite', done: true }
      };

      // if onboarding data didn't exist (likely a new SSO user instead of existing one), set other settings:
      if (!userData.onboarding) {
        updateData.email_notifications = email_notifications;
        updateData.settings = settings;
      }

      await updateUser(updateData, userData.id);
    }

    // if no user onboarding data exists, add the default onboarding flow. The AuthRoute will automatically redirect the user:
    else if (!userData.onboarding) {
      await updateUser({
        onboarding          : { flow: 'mvp_free_plan', step: 'name_business' },
        email_notifications,
        settings,
      }, userData.id);

      return;
    }

    // user already existed or had the onboarding set
    else {
      await dispatch({ type: 'currentUser/set', payload: userData });
    }

    // TODO -> register maybe should not allow users to redirect because we have an onboarding flow
    const redirectTo = searchParams.get('redirect_to');

    if (redirectTo) {
      navigate(redirectTo ? decodeURIComponent(redirectTo) : '/');
    }
  }

  const login = useGoogleLogin({
    onSuccess: onGoogleLogin,
  });

  return (
    <Form className='__auth-form' onSubmit={handleSubmit} noValidate validated={validated && !errors}>
      { !hideTitle && <h2><T>{ isLogin ? 'Log In' : 'Sign Up' }</T></h2> }

      <button type="button" className="google-sso" onClick={login}>
        <GoogleLogo />
        <span>{ isLogin ? 'Sign in with Google' : 'Sign up with Google' }</span>
      </button>

      <hr />

      { IS_DEV && isLogin && (
        <>
          <div role='button' className="dev-accounts" onClick={toggleShowDevUsers}>{ showDevUsers ? 'Hide' : 'Show' } Dev Users</div>
          { showDevUsers && (
            <Form.Group className='language-select my-4'>
              <Form.Label>User</Form.Label>
              <Form.Select value={devUser} onChange={selectDevUser}>
                { DEV_USERS.map(user => (
                  <option key={user} disabled={user === devUser} value={user}>{ user }</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
        </>
      )}

      { !isLogin && (
        <div className="name-inputs">
          <Form.Group className="field">
            <Form.Control type="text" placeholder={t('First Name')} required value={firstName} onChange={e => setFirstName(e.target.value)} isValid={validated && !errors?.first_name} isInvalid={errors?.first_name} />
            { errors?.first_name?.map(error => <Form.Control.Feedback key={error} type="invalid"><T>{ error }</T></Form.Control.Feedback> )}
          </Form.Group>

          <Form.Group className="field">
            <Form.Control type="text" placeholder={t('Last Name')} required value={lastName} onChange={e => setLastName(e.target.value)} isValid={validated && !errors?.last_name} isInvalid={errors?.last_name} />
            { errors?.last_name?.map(error => <Form.Control.Feedback key={error} type="invalid"><T>{ error }</T></Form.Control.Feedback> )}
          </Form.Group>
        </div>
      )}

      <Form.Group className="field">
        <Form.Control type="email" placeholder={t('Email address')} required value={email} onChange={e => setEmail(e.target.value)} isValid={validated && !errors?.email} isInvalid={errors?.email} />
        { errors?.email?.map(error => <Form.Control.Feedback key={error} type="invalid"><T>{ error }</T></Form.Control.Feedback> )}
      </Form.Group>

      <Form.Group className="field">
        <PasswordInput placeholder={t('Password')} minLength={6} required value={password} onChange={e => setPassword(e.target.value)} isValid={validated && !errors?.password} isInvalid={errors?.password} feedback={ errors?.password?.map(error => <Form.Control.Feedback key={error} type="invalid"><T>{ error }</T></Form.Control.Feedback> )} />
      </Form.Group>

      { isLogin && <p><Link to='../reset' relative="path"><T>Forgot your password?</T></Link></p> }

      <div className="action">
        <button disabled={loading} type='submit'>
          { loading
          ? <Spinner as="span" animation="border" size="sm" />
          : ( signUpText || <T i18nKey={isLogin ? 'log-in-action' : 'sign-up-action' }/> )
          }
        </button>
        { !isLogin && !bare && <div className="no-credit-card">No credit card required</div> }

        <div className="request-error">
          { errors?.non_field_errors?.map(error => <div key={error}>{ error }</div>) || '\u00A0' }
          { errors?.error === 'invalid_grant' && <div className="request-error">Your email or password is incorrect</div> }
          { errors?.error !== 'invalid_grant' && <div className="request-error">{ errors?.error }</div> }
        </div>

      </div>

      <p className='create-account'>
        { isLogin
        ? <T i18nKey='create-free-account'>Don't have an account? <Link to={{ pathname: fromInvite ? '/register' : '../signup', search: location.search }} relative="path">Create a free acount</Link></T>
        : <T i18nKey='have-account-login'>Already have an account? <Link to={{ pathname: '../login', search: location.search }} relative="path">Log in</Link></T>
        }
      </p>
    </Form>
  )
}
